import {
  ArrowRight,
  ArrowsLeftRight,
  ChartBar,
  Megaphone,
  Money,
} from "@phosphor-icons/react";
import { Badge } from "flowbite-react";
import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { ColumnDef } from "@tanstack/react-table";

import { Table } from "../../../../components/Table";
import { useMetaAdsCampaignDetail } from "../../../../hooks/get/useMetaAdsCampaignDetail";
import { useMetaAdsActivityHistory } from "../../../../hooks/get/useMetaAdsActivityHistory";
import { useDateRange } from "../../../../hooks";
import { useIsMobile } from "../../../../hooks";
import { IDateRangePreset } from "../../../../interfaces";
import { getCurrencySymbol } from "../../../../lib/utils";
import { CurrencyCode } from "../../../../shared/constants";
import { IMetaCampaignHistory } from "../../interface/metaAds";
import BaseCampaignHistory from "../../components/shared/BaseCampaignHistory";

const MetaAdsHistory: React.FC = () => {
  const { campaign_id } = useParams<{ campaign_id: string }>();
  const isMobile = useIsMobile();
  const { dateRange, setDateRange, selectedPreset, setSelectedPreset } = useDateRange();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  const dateParams = useMemo(() => ({
    start_date: dateRange.startDate.toISOString().split("T")[0],
    end_date: dateRange.endDate.toISOString().split("T")[0],
    campaign_id: campaign_id || "",
  }), [dateRange.startDate, dateRange.endDate, campaign_id]);

  const { data: campaignData, isLoading: isCampaignLoading } = useMetaAdsCampaignDetail({
    campaign_id: campaign_id || "",
    startDate: dateParams.start_date,
    endDate: dateParams.end_date,
  });

  const { data: historyData, isLoading: isHistoryLoading } = useMetaAdsActivityHistory({
    params: dateParams,
  });

  const isLoading = isCampaignLoading || isHistoryLoading;
  const campaign = campaignData?.campaign;

  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    setDateRange(startDate, endDate);
  };

  const handlePresetChange = (preset: IDateRangePreset | undefined) => {
    setSelectedPreset(preset);
  };

  const filteredHistory = useMemo(() => {
    const dataToFilter = historyData?.history || [];
    if (!searchTerm) return dataToFilter;
    const lowerSearch = searchTerm.toLowerCase();

    const getValueString = (val: any) => {
      if (typeof val === 'object' && val !== null) {
        return (val.new_value || val.old_value || "").toString().toLowerCase();
      }
      return (val || "").toString().toLowerCase();
    };

    return dataToFilter.filter(
      (item: IMetaCampaignHistory) =>
        (item.activity_type || "").toLowerCase().includes(lowerSearch) ||
        (item.field_changed || "").toLowerCase().includes(lowerSearch) ||
        getValueString(item.previous_value).includes(lowerSearch) ||
        getValueString(item.new_value).includes(lowerSearch),
    );
  }, [historyData, searchTerm]);

  const columns = useMemo<ColumnDef<IMetaCampaignHistory>[]>(
    () => [
      {
        accessorKey: "updated_at",
        header: t("common.columns.dateTime", "Date & Time"),
        cell: ({ row }) => (
          <div className="text-sm text-gray-500 dark:text-gray-400 font-medium tabular-nums">
            {dayjs(row.original.changed_at || row.original.updated_at).format("MMM DD, YYYY HH:mm")}
          </div>
        ),
      },
      {
        accessorKey: "change_type",
        header: t("common.columns.changeType", "Change Type"),
        cell: ({ row }) => {
          const type = row.original.activity_type || row.original.change_type;
          const field = row.original.field_changed;
          let color = "info";
          let icon = <ChartBar size={14} weight="duotone" />;

          if (type?.includes("BUDGET") || field?.includes("budget")) {
            color = "success";
            icon = <Money size={14} weight="duotone" />;
          } else if (type?.includes("BID") || field?.includes("bid")) {
            color = "warning";
            icon = <ArrowsLeftRight size={14} weight="duotone" />;
          } else if (type?.includes("STATUS") || field?.includes("status")) {
            color = "purple";
            icon = <Megaphone size={14} weight="duotone" />;
          }

          return (
            <div className="flex items-center gap-2">
              <Badge
                color={color}
                size="xs"
                className="px-2 py-0.5 rounded-md font-medium uppercase tracking-wider"
              >
                <div className="flex items-center gap-1">
                  {icon}
                  {(type || "").replace("_CHANGE", "").replace("_", " ")}
                </div>
              </Badge>
            </div>
          );
        },
      },
      {
        accessorKey: "field_changed",
        header: t("common.columns.field", "Field"),
        cell: ({ row }) => (
          <div className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-tighter">
            {row.original.field_changed || "-"}
          </div>
        ),
      },
      {
        id: "changes",
        header: t("common.columns.changes", "Changes"),
        cell: ({ row }) => {
          const formatValue = (val: any) => {
            if (typeof val === 'object' && val !== null) {
              if (val.type === 'payment_amount') {
                const amount = val.new_value ?? val.old_value ?? 0;
                return `${getCurrencySymbol(val.currency as CurrencyCode)}${amount.toLocaleString()} ${val.additional_value || ""}`;
              }
              return JSON.stringify(val);
            }
            return val?.toString() || "-";
          };

          return (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-400 dark:text-gray-500 font-medium line-through decoration-gray-300 tabular-nums">
                {formatValue(row.original.previous_value ?? row.original.old_value)}
              </span>
              <ArrowRight size={12} className="text-gray-400 dark:text-gray-500" weight="bold" />
              <span className="text-gray-800 dark:text-gray-100 font-bold tabular-nums">
                {formatValue(row.original.new_value)}
              </span>
            </div>
          );
        },
      },
    ],
    [t],
  );

  return (
    <BaseCampaignHistory
      headerProps={{
        title: campaign?.campaign_name || t("campaigns.history.title", "Campaign History"),
        platform: "meta_ads",
        type: "history",
        appInfo: {
          id: campaign?.meta_campaign_id || campaign_id || "",
          name: campaign?.app_name || "",
          logoUrl: campaign?.app_logo || null,
        },
        onDateRangeChange: handleDateRangeChange,
        onPresetChange: handlePresetChange,
        dateRange: dateRange,
        selectedPresetId: selectedPreset?.id,
        onSearch: setSearchTerm,
        searchPlaceholder: t("common.search", "Search logs..."),
      }}
    >
      <Table
        data={filteredHistory}
        columns={columns}
        isLoading={isLoading}
        isMobile={isMobile}
        loadingHeight={4}
          loadingRowCount={5}
      />
    </BaseCampaignHistory>
  );
};

export default MetaAdsHistory;
